import { timing, variantForLevel } from './params';
const PRESENT_DURATION_MS = 500;
const MIN_DISTANCE = 0.14; // distance minimale entre points en coord [0,1]
const MAX_PLACEMENT_ATTEMPTS = 200;
function randomPoint() {
    // Marge 0.06 pour eviter overflow visuel.
    return {
        x: 0.06 + Math.random() * 0.88,
        y: 0.06 + Math.random() * 0.88,
    };
}
function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}
function placeTargets(n) {
    const out = [];
    // Adapte la distance min selon densite pour rester possible avec N grand.
    const minDist = n <= 10 ? MIN_DISTANCE : n <= 18 ? 0.11 : 0.09;
    for (let id = 1; id <= n; id += 1) {
        let placed = false;
        for (let attempt = 0; attempt < MAX_PLACEMENT_ATTEMPTS; attempt += 1) {
            const p = randomPoint();
            let ok = true;
            for (const existing of out) {
                if (distance(p, existing) < minDist) {
                    ok = false;
                    break;
                }
            }
            if (ok) {
                out.push({ id, x: p.x, y: p.y });
                placed = true;
                break;
            }
        }
        if (!placed) {
            // Fallback : ajoute sans contrainte (rare, grilles tres denses).
            const p = randomPoint();
            out.push({ id, x: p.x, y: p.y });
        }
    }
    return out;
}
export function makeStimulus(trialIdx, value, level) {
    const variant = variantForLevel(level);
    // value pilote le n_items via staircase, plancher sur variant.nTargets ?
    // On respecte la valeur staircase mais on clamp dans [5, 25] (config).
    const nTargets = Math.max(5, Math.min(25, Math.round(value)));
    // variant.nTargets influe sur layout density via placement (deja gere
    // par placeTargets selon n). Variant garde son role pour metadata level.
    void variant;
    const targets = placeTargets(nTargets);
    return {
        trialIdx,
        value,
        targets,
        nTargets,
    };
}
export function createTrailMakingAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        nextExpectedId: 0,
        errors: 0,
        visitedIds: [],
        feedback: null,
        level,
        variant: variantForLevel(level),
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let inputStart = 0;
    const timers = [];
    function clearTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function settle() {
        if (!resolver || !state.stim)
            return;
        const completionTimeMs = Date.now() - inputStart;
        const errors = state.errors;
        const correct = errors === 0;
        const r = resolver;
        resolver = null;
        rejecter = null;
        r({
            errors,
            completionTimeMs,
            correct,
            reactionTimeMs: completionTimeMs,
        });
    }
    return {
        mount() {
            // Idempotent, rendu declaratif cote React.
        },
        unmount() {
            clearTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                update({
                    subPhase: 'fixation',
                    stim,
                    nextExpectedId: 1,
                    errors: 0,
                    visitedIds: [],
                    feedback: null,
                });
                timers.push(setTimeout(() => {
                    update({ subPhase: 'presenting' });
                    timers.push(setTimeout(() => {
                        update({ subPhase: 'awaiting_input' });
                        inputStart = Date.now();
                    }, PRESENT_DURATION_MS));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handleTap(targetId) {
            if (state.subPhase !== 'awaiting_input' || !state.stim)
                return;
            if (targetId === state.nextExpectedId) {
                const newVisited = [...state.visitedIds, targetId];
                const newNext = targetId + 1;
                update({
                    visitedIds: newVisited,
                    nextExpectedId: newNext,
                });
                if (newVisited.length >= state.stim.nTargets) {
                    settle();
                }
            }
            else {
                update({ errors: state.errors + 1 });
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map